import { z } from 'zod';

// Anonymous sales/wishlist-request submissions from the public CMS
// (apps/cms-public). Validation mirrors the legacy AIT-CMS
// `/api/wishlist-request` route (string max-lengths, numeric bounds, ISO
// date format) so existing front-end forms can submit unchanged.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const CURRENCY_RE = /^[A-Z]{3}$/;

export const salesRequestContactInfoSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  email: z.string().trim().max(320).regex(EMAIL_RE),
  country: z.string().trim().max(100).optional(),
  company: z.string().trim().max(200).optional(),
  phone: z.string().trim().max(50).optional(),
});
export type SalesRequestContactInfo = z.infer<
  typeof salesRequestContactInfoSchema
>;

// The public form posts its numbers as strings: it is a react-hook-form
// `register()` on plain `<input>` / `<select>` elements, which hands back the
// element's value verbatim, and the field types claiming `number` never made
// that true at runtime (XT-761 — deccana.com submissions were refused with
// "expected number, received string" on `adultsAges` / `childrenCount`).
//
// Legacy AIT-CMS accepted those strings, and this schema exists to let
// existing form bundles submit unchanged, so a numeric string is coerced here
// rather than refused. The bounds below still apply — coercion widens what the
// endpoint accepts, never what it stores. A blank field reads as "not
// answered" rather than 0, which a bare `z.coerce.number()` would have done.
const toNumber = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  // A non-numeric string falls through unchanged so the caller still gets
  // "expected number, received string" rather than a confusing NaN error.
  return Number.isFinite(parsed) ? parsed : value;
};

/** Optional number that also accepts the form's numeric strings. */
const optionalNumber = (schema: z.ZodNumber) =>
  z.preprocess(toNumber, schema.optional());

/**
 * Optional list of ages that also accepts numeric strings, and drops blank
 * entries: the form renders one age control per traveller, so a visitor who
 * adds a row and leaves its age unset would otherwise have the whole enquiry
 * refused over a field they never filled.
 */
const optionalAgeList = (maxAge: number) =>
  z.preprocess(
    (value) =>
      Array.isArray(value)
        ? value.map(toNumber).filter((entry) => entry !== undefined)
        : value,
    z.array(z.number().int().min(0).max(maxAge)).max(50).optional()
  );

export const salesRequestWishListInfoSchema = z.object({
  undated: z.boolean().optional(),
  startTime: z.iso.datetime().optional(),
  finishTime: z.iso.datetime().optional(),
  durationDays: optionalNumber(z.number().int().min(1).max(365)),
  budgetFrom: optionalNumber(z.number().int().min(0).max(10_000_000)),
  budgetTo: optionalNumber(z.number().int().min(0).max(10_000_000)),
  adultsCount: optionalNumber(z.number().int().min(0).max(50)),
  adultsAges: optionalAgeList(120),
  childrenCount: optionalNumber(z.number().int().min(0).max(50)),
  childrenAges: optionalAgeList(17),
  message: z.string().trim().max(5000).optional(),
  currency: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .pipe(z.string().regex(CURRENCY_RE))
    .default('EUR'),
});
export type SalesRequestWishListInfo = z.infer<
  typeof salesRequestWishListInfoSchema
>;

// `_hp` is a honeypot — the form renders it as a hidden field; bots
// fill it indiscriminately. The mutation rejects any non-empty value.
// `_fillMs` is the wall-clock elapsed-since-mount in milliseconds; the
// mutation rejects implausibly fast submissions (legacy AIT-CMS used
// the `x-form-fill-ms` HTTP header for the same purpose — porting as a
// body field keeps the surface JSON-only). Both fields are optional so
// direct API callers / older form bundles still work; the production
// form always sends them.
export const createSalesRequestInputSchema = z.object({
  contactInfo: salesRequestContactInfoSchema,
  wishListInfo: salesRequestWishListInfoSchema.optional(),
  _hp: z.string().max(0).optional(),
  _fillMs: z.number().int().nonnegative().optional(),
});
export type CreateSalesRequestInput = z.infer<
  typeof createSalesRequestInputSchema
>;

export const createSalesRequestOutputSchema = z.object({
  id: z.uuid(),
});
export type CreateSalesRequestOutput = z.infer<
  typeof createSalesRequestOutputSchema
>;
